import * as a from '@mitranim/js/all.mjs'
import * as c from './conf.mjs'
import * as uv from './util-view.mjs'
import * as ud from './util-dom.mjs'

// Semi-placeholder.
export class ErrHttp extends a.ErrHttp {}

// Similar to `ErrHttp` but supports additional non-HTTP code.
export class Err extends ErrHttp {
  constructor({message, status, res, code}) {
    a.reqStr(message)
    super((code ? `${code}: ` : ``) + message, status, res)
    this.code = code
  }
}

export class ErrRedir extends ErrHttp {
  constructor(status, loc) {
    loc = a.render(loc)
    super(loc, status)
    this.location = a.reqValidStr(a.render(loc))
  }

  resRedir() {
    const {status, location} = this
    return new Response(undefined, {status, headers: {location}})
  }
}

export function errRedir(location) {
  return new ErrRedir(c.HTTP_MOVED_PERMANENTLY, location)
}

export function errRedirTemp(location) {
  return new ErrRedir(c.HTTP_SEE_OTHER, location)
}

export class ErrHttpTimeout extends ErrHttp {
  constructor(timeout) {
    timeout = a.laxNat(timeout)
    super(`HTTP request timed out${timeout ? ` after ${timeout}ms` : ``}`, 0)
  }
}

export class ErrNotFound extends ErrHttp {
  constructor(msg) {
    super(a.laxStr(msg) || `not found`, c.HTTP_NOT_FOUND)
  }
}

export function errNotFound(msg) {return new ErrNotFound(msg)}

export function errStrRes(err) {
  return new Response(errStr(err), {status: errHttpStatus(err)})
}

export function errStrSimple(err) {
  return a.laxStr(err && (err.message || err.code || String(err)))
}

export function errStr(err) {
  return a.laxStr(err?.stack) || errStrSimple(err)
}

export function skipErr(err, fun) {
  a.reqFun(fun)
  if (a.isNil(err) || fun(err)) return undefined
  throw err
}

export function isErrIrrelevant(err) {return a.isNil(err) || a.isErrAbort(err)}
export function skipErrIrrelevant(err) {return skipErr(err, isErrIrrelevant)}

export function isErrUnauthed(err) {return a.hasStatus(err, c.HTTP_UNAUTHORIZED)}
export function skipErrUnauthed(err) {return skipErr(err, isErrUnauthed)}

export function isErrForbidden(err) {return a.hasStatus(err, c.HTTP_FORBIDDEN)}
export function skipErrForbidden(err) {return skipErr(err, isErrForbidden)}

export function isErrNotFound(err) {return a.hasStatus(err, c.HTTP_NOT_FOUND)}
export function skipErrNotFound(err) {return skipErr(err, isErrNotFound)}

export function isErrUnauthenticatedExternal(err) {return a.hasStatus(err, c.HTTP_STATUS_UNAUTHENTICATED_EXTERNAL)}

export function isErrUnauthorizedExternal(err) {return a.hasStatus(err, c.HTTP_STATUS_UNAUTHORIZED_EXTERNAL)}

export function isErrAuthExternal(err) {
  return isErrUnauthenticatedExternal(err) || isErrUnauthorizedExternal(err)
}

export function isErrRequiresPract(err) {
  return a.isInst(err, Err) && err.code === c.ERR_CODE_REQUIRES_PRACT
}

export function isErrAbort(err) {
  if (!a.isInst(err, Error)) return false
  const {code, name} = err

  return (
    // Node-specific.
    code === `ABORT_ERR` ||

    // Standard web API.
    name === `AbortError` ||

    // Deno-specific.
    name === `BadResource` ||

    /*
    Left here as an example of what NOT to do.
    A request timeout error is NOT a cancelation error.
    It indicates that something went wrong.

    name === 'ErrHttpTimeout' ||
    */

    isErrDisconn(err)
  )
}

export function isErrDisconn(err) {
  // Deno-specific.
  return a.isInst(err, Error) && (
    err.name === `ConnectionAborted` ||
    (err.name === `Http` && (
      err.message.includes(`connection closed`) ||
      err.message.includes(`Connection reset by peer`) ||
      err.message.includes(`Broken pipe`)
    ))
  )
}

export function errHttpStatus(err) {
  return a.getStatus(err) || c.HTTP_INTERNAL_SERVER_ERROR
}

export function errImplementInSubclass() {
  return Error(`must be implemented in subclass`)
}

/*
Workaround for a problem inherent to bundle splitting and async imports: when
deploying a new version of the app, we delete chunks from the previous version,
but the clients' browsers still have OLD bundles that attempt to load old
chunks. The resulting error may manifest in various ways, including but limited
to:

  * 404 network error, resulting in a native module import error. TODO: is this
    error standardized between browsers?

  * Module ID mismatch, producing an obscure JS exception whose content varies
    between browsers.

It's currently unclear if there's a single way to detect all possible resulting
errors. This is the result.
*/
export function onImportErr(err) {
  uv.tryRefreshOnce()
  errNoticeShow(err)
  throw err
}

/*
This must be in the same module as `onImportErr`.
What if another module fails to import?
TODO consolidate with `Notif`.
*/
function errNoticeShow() {
  if (!c.DOM_AVAILABLE) return
  errNoticeHide()
  document.body.append(ClientFailureNotice())
}

function ClientFailureNotice() {
  return uv.E(
    `div`,
    {class: `col-sta-sta font-large bg-error fg-white round fix-t-r z-index-modal mar-t mar-r pad gap`},
    uv.E(`p`, {class: `wei-semi-bold`}, `Sorry! Something has gone wrong.`),
    uv.E(`p`, {}, `Please refresh the page (Ctrl+R or Cmd+R).`),
  )

  // return prt.Notice(
  //   {class: `--md`, kind: c.NOTICE_ERROR},
  //   uv.E(`p`, {class: `wei-semi-bold`}, `Sorry! Something has gone wrong.`),
  //   uv.E(`p`, {}, `Please refresh the page (Ctrl+R or Cmd+R).`)
  // )
}

function errNoticeHide() {ud.elemById(c.NOTIF_ID)?.remove()}

export function errUnreachable() {return Error(`unreachable`)}

/*
Should be used only for caught and handled errors, such as errors rendered as
HTML / DOM. We don't install `window.Sentry` in SSR, so this is automatically
browser-only.
*/
export function sentryCaptureException(err) {
  return err && window.Sentry?.captureException?.(err)
}
